<template>
  <div>
    <!-- Label -->
    <label class="text-black font-medium text-[14px] mb-1 block">
      Tautkan email partner
    </label>

    <!-- Search Input + Partner Dropdown -->
    <div class="relative w-full">
      <v-select
        v-model="selectedEmailTemp"
        :options="filteredEmailOptions"
        :filterable="false"
        :clearable="false"
        placeholder="Cari email"
        label="email"
        :selectable="() => value.length < maxEmails"
        @search="onSearch"
        @input="onSelectEmail"
      >
        <template #no-options>
          <span class="text-[#6B7280] text-[13px] px-3">
            Email tidak ditemukan
          </span>
        </template>
      </v-select>

      <!-- Dropdown Partner -->
      <div
        ref="partnerDropdownRef"
        class="absolute right-3 z-10"
        style="top: 50%; transform: translateY(-50%);"
      >
        <button
          type="button"
          class="flex items-center gap-1 text-[#4B5563] text-[14px] font-normal focus:outline-none bg-transparent border-0 p-0 cursor-pointer"
          @click.stop="togglePartnerDropdown"
        >
          <span>{{ selectedPartnerLabel }}</span>
          <feather-icon
            :icon="isPartnerDropdownOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            size="16"
            class="text-[#6B7280]"
          />
        </button>

        <!-- Dropdown Menu Options -->
        <div
          v-if="isPartnerDropdownOpen"
          class="absolute right-0 top-full mt-2 w-[160px] bg-white rounded-2xl shadow-lg border border-[#E5E5E5] p-1.5 z-50"
        >
          <div
            v-for="option in partnerOptions"
            :key="option.value"
            class="p-[5px] text-[14px] cursor-pointer rounded-xl transition-colors"
            :class="selectedPartner === option.value ? 'bg-[#F3F4F6] text-black font-medium' : 'text-[#6B7280] hover:bg-[#F3F4F6] hover:text-black'"
            @click.stop="onSelectPartner(option.value)"
          >
            {{ option.label }}
          </div>
        </div>
      </div>
    </div>

    <!-- Max Warning -->
    <div
      v-if="value.length >= maxEmails"
      class="text-[#E31A1A] text-[12px] mt-1"
    >
      Maksimal {{ maxEmails }} akun dapat dipilih dalam 1 penautan
    </div>

    <!-- Selected Emails Chips -->
    <div
      v-if="value.length"
      class="flex flex-wrap gap-2 mt-2"
    >
      <div
        v-for="(email, index) in value"
        :key="index"
        class="inline-flex items-center gap-1 bg-[#F3F4F6] rounded-[20px] px-3 py-[6px] text-[13px] text-black"
      >
        <span class="break-all">{{ email.email }}</span>
        <button
          class="btn p-0 ml-1 leading-none"
          @click="removeEmail(index)"
        >
          <feather-icon
            icon="XIcon"
            size="14"
            class="text-[#6B7280]"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { newAxiosIns } from '@/libs/axios'
import vSelect from 'vue-select'

export default {
  components: {
    vSelect,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedEmailTemp: null,
      maxEmails: 12,
      maxDropdownItems: 5,
      emailOptions: [],
      searchTimeout: null,
      selectedPartner: 'komerce',
      partnerOptions: [{
        label: 'Komerce',
        value: 'komerce',
      }, {
        label: 'Raja Ongkir',
        value: 'ro',
      }, {
        label: 'Komplace',
        value: 'komplace',
      }],
      isPartnerDropdownOpen: false,
    }
  },
  computed: {
    filteredEmailOptions() {
      const selectedEmailValues = this.value.map(e => e.email)
      return this.emailOptions
        .filter(option => !selectedEmailValues.includes(option.email))
        .slice(0, this.maxDropdownItems)
    },
    selectedPartnerLabel() {
      const option = this.partnerOptions.find(item => item.value === this.selectedPartner)
      return option ? option.label : this.selectedPartner
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    handleClickOutside(e) {
      if (this.$refs.partnerDropdownRef && !this.$refs.partnerDropdownRef.contains(e.target)) {
        this.isPartnerDropdownOpen = false
      }
    },
    togglePartnerDropdown() {
      this.isPartnerDropdownOpen = !this.isPartnerDropdownOpen
    },
    onSelectPartner(option) {
      this.selectedPartner = option
      this.isPartnerDropdownOpen = false
    },
    onSearch(search, loading) {
      if (search.length >= 3) {
        if (loading) loading(true)
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout)
        }
        this.searchTimeout = setTimeout(() => {
          this.getListEmail(search, loading)
        }, 500)
      } else {
        this.emailOptions = []
      }
    },
    getListEmail(search, loading) {
      const url = '/auth/api/v1/admin/partner/search-by-email'
      const params = {
        total_data: 10,
        keyword: search,
        ref: 'organization',
        product: this.selectedPartner,
      }

      newAxiosIns
        .get(url, { params })
        .then(res => {
          this.emailOptions = res.data.data || []
        })
        .catch(err => {
          const message = err?.response?.data?.meta?.message
          if (message) {
            const capitalize = message.charAt(0).toUpperCase() + message.slice(1)
            this.$toast_error({ message: capitalize })
          } else if (err?.message) {
            this.$toast_error({ message: err.message })
          }
        })
        .finally(() => {
          if (loading) loading(false)
        })
    },
    onSelectEmail(val) {
      if (val && this.value.length < this.maxEmails) {
        const updated = [...this.value, {
          ...val,
          product: val.product || this.selectedPartner,
        }]
        this.$emit('input', updated)
      }
      this.$nextTick(() => {
        this.selectedEmailTemp = null
      })
    },
    removeEmail(index) {
      const updated = [...this.value]
      updated.splice(index, 1)
      this.$emit('input', updated)
    },
    reset() {
      this.selectedEmailTemp = null
      this.emailOptions = []
      this.selectedPartner = 'komerce'
      this.isPartnerDropdownOpen = false
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
::v-deep .vs__dropdown-toggle {
  border-radius: 8px !important;
  border-color: #E5E5E5 !important;
  padding: 4px 120px 4px 8px !important;
  min-height: 42px !important;
}

::v-deep .vs__open-indicator {
  display: none !important;
}

::v-deep .vs__search::placeholder {
  color: #9CA3AF !important;
}

::v-deep .vs__dropdown-menu {
  border-radius: 8px !important;
  border-color: #E5E5E5 !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
  max-height: 240px !important;
}

::v-deep .vs__dropdown-option {
  padding: 10px 16px !important;
  color: #333 !important;
}

::v-deep .vs__dropdown-option--highlight {
  background: #F3F4F6 !important;
  color: #333 !important;
}
</style>
